// ============================================================================
// OMS PO — a flat, searchable list of every allocation (allotment / PO), split
// into Pending delivery (goods not yet with the customer) and All. A row click
// opens the underlying OMS order (/oms/:salesOrderId). Self-contained module.
// ============================================================================
import { ReloadOutlined, ShoppingOutlined } from "@ant-design/icons";
import { Badge, Button, Empty, Input, Select, Space, Table, Tabs, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CopyText from "../components/copy_text";
import DocPdfButtons, { hasDocPdfs } from "../components/doc_pdf_buttons";
import ExpressTag from "../components/express_tag";
import UnloadingTag from "../components/unloading_tag";
import StatusTag from "../components/status_tag";
import { DeliveryStatusTag } from "../logistics/components/logistics_status_tag";
import { fmtText, omsOrderPath } from "../format";
import { useRememberedTab } from "../remembered_tab";
import styles from "../index.module.scss";
import { listPurchaseOrders, PoRow, PoTab, PoTabCounts } from "./po.api";
import { listWarehouses, WarehouseOption } from "../logistics/logistics.api";

const PAGE_SIZE = 50;

// SEARCH is a tab of its own and the one this screen opens on by default — ops come here holding a PO
// number a supplier just read out far more often than they come to read a 50-row board. It is the only
// tab that is not a cut of the data: it issues no query at all until something is typed. After the
// first visit the operator's own last tab wins instead (see `remembered_tab.ts`).
const SEARCH_TAB = "search";
type PoPageTab = PoTab | typeof SEARCH_TAB;
const TAB_STORAGE_KEY = "oms.po.tab";
const TAB_KEYS: PoPageTab[] = [SEARCH_TAB, "pending_delivery", "delayed", "due_today", "all"];

const dash = <span style={{ color: "#bbb" }}>—</span>;

// The order page opens on the leg the operator clicked: `?allocation=<id>` makes that card
// expand and scroll itself into view. Keyed on the allocation id rather than the PO number
// because a planned allotment has no PO number yet, and the id is always there.
const orderPath = (row: PoRow) => omsOrderPath(String(row.sales_order_id), { allocationId: row.id });

const openOrder = (row: PoRow) =>
  row.sales_order_id && window.open(orderPath(row), "_blank", "noopener,noreferrer");

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const PoTable: React.FC<{
  tab: PoTab;
  warehouseId?: number;
  onCounts: (counts: PoTabCounts) => void;
  // The Search tab's table: same rows, same columns, but it fetches nothing until there is a query,
  // and it does not publish tab counts (they come back scoped to the search, so they would label the
  // other tabs with "how many matched" — a number that reads as the size of each cut and is not).
  searchOnly?: boolean;
}> = ({ tab, warehouseId, onCounts, searchOnly }) => {
  const [rows, setRows] = useState<PoRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Type-to-search (debounced) — the only sane way to reach a PO without paging 50 rows at a time.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch((prev) => {
        const next = searchInput.trim();
        if (next !== prev) setPage(1);
        return next;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [warehouseId]);

  const load = useCallback(async () => {
    // No query, no request — arriving on the Search tab costs one render.
    if (searchOnly && !search) {
      setRows([]);
      setCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await listPurchaseOrders({
      tab,
      warehouse_id: warehouseId,
      search: search || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    });
    if (res) {
      setRows(res.results);
      setCount(res.count);
      if (res.tab_counts && !searchOnly) onCounts(res.tab_counts);
    }
    setLoading(false);
  }, [tab, warehouseId, search, page, onCounts, searchOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: ColumnsType<PoRow> = [
    {
      title: "PO Number",
      dataIndex: "po_number",
      width: 150,
      fixed: "left",
      // Click-to-copy: the PO number is what an operator reads out on a supplier call, and
      // retyping it off the screen is where transcription errors come from.
      render: (v) => (
        v ? (
          <CopyText value={v} label="PO number">
            <strong>{v}</strong>
          </CopyText>
        ) : (
          dash
        )
      ),
    },
    {
      title: "Order (Lead ID)",
      dataIndex: "lead_id",
      width: 220,
      render: (v, row) => (
        <Space size={4} wrap>
          {v ? <CopyText value={v} label="Lead ID" /> : dash}
          {/* A real anchor, so native right-click → "open in new tab" works and a plain click does
              not double-fire with the row handler (both open the same tab anyway). */}
          {row.sales_order_id ? (
            <Link
              to={orderPath(row)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: 12 }}
            >
              open
            </Link>
          ) : null}
          <ExpressTag show={row.is_express} />
          <UnloadingTag show={row.is_unloading} />
        </Space>
      ),
    },
    {
      title: "SO Number",
      dataIndex: "zoho_salesorder_number",
      width: 130,
      render: (v) => v || dash,
    },
    { title: "Client", dataIndex: "client_name", width: 150, render: (v) => v || dash },
    { title: "Supplier", dataIndex: "supplier_name", width: 160, render: (v) => v || dash },
    {
      title: "Type",
      dataIndex: "shipment_type",
      width: 120,
      render: (v) => (v ? fmtText(v) : dash),
    },
    {
      title: "OMS Status",
      dataIndex: "state",
      width: 130,
      // Hovering explains the state: the live error overlay across this allotment's legs, or the
      // reason it is held. Costs no extra query — the server folds it from stages it already loads.
      render: (s, row) => (
        <StatusTag
          status={s}
          tooltip={row.error_message || row.hold_reason}
          errorKind={row.error_kind}
        />
      ),
    },
    {
      // Delivery progress on its own axis — where the goods physically are, independent of the
      // fulfilment/OMS status (which folds procurement, invoicing, holds, errors).
      title: "Delivery",
      dataIndex: "delivery_status",
      width: 130,
      render: (v) => <DeliveryStatusTag status={v} />,
    },
    { title: "Items", dataIndex: "item_count", width: 70, align: "right" },
    {
      // The two documents an operator is chased for by PO number — the PO and the delivery challan.
      // Streamed from Zoho by the backend, so no Zoho login: a vendor call asking "send me the DC
      // for MD92026…" is answered from this row instead of from inside the order.
      title: "Docs",
      key: "documents",
      width: 130,
      render: (_, row) =>
        hasDocPdfs(row.documents) ? <DocPdfButtons docs={row.documents} /> : dash,
    },
    {
      title: "Target delivery",
      dataIndex: "target_delivery_date",
      width: 140,
      render: fmtDate,
    },
    { title: "Created", dataIndex: "created_at", width: 130, render: fmtDate },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap align="end">
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Search</div>
          <Input.Search
            // Land the cursor in the box they came here to type in.
            autoFocus={searchOnly}
            placeholder="PO number or ENQ…"
            allowClear
            style={{ width: 320 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(v) => {
              setPage(1);
              setSearch(v.trim());
            }}
          />
        </div>
        {search && (
          <Button
            onClick={() => {
              setPage(1);
              setSearchInput("");
              setSearch("");
            }}
          >
            Clear
          </Button>
        )}
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </Space>
      <div style={{ color: "#8c8c8c", fontSize: 13, marginTop: -8 }}>
        {searchOnly && !search
          ? "Type a PO number or ENQ above to find an allotment"
          : count === 0
            ? "No POs match"
            : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, count)} of ${count} PO${count === 1 ? "" : "s"}`}
        {search ? ` for “${search}”` : ""}
      </div>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: "max-content" }}
        locale={
          searchOnly && !search
            ? {
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Search for a PO to begin"
                  />
                ),
              }
            : undefined
        }
        onRow={(record) => ({
          // Left-click and middle-click both open the order in a NEW tab, matching the OMS order
          // list: the board is a work list an operator returns to repeatedly, and navigating away
          // loses their place, scroll position and filters. (The Lead ID cell is a real link too,
          // so native right-click → "open in new tab" works.)
          onClick: () => openOrder(record),
          onAuxClick: (e) => {
            if (e.button === 1) openOrder(record);
          },
          style: { cursor: record.sales_order_id ? "pointer" : "default" },
        })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: count,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />
    </Space>
  );
};

const OmsPoDashboard: React.FC = () => {
  const [tab, setTab] = useRememberedTab<PoPageTab>(TAB_STORAGE_KEY, TAB_KEYS, SEARCH_TAB);
  const [counts, setCounts] = useState<PoTabCounts | null>(null);
  // The fulfilment-warehouse scope lives on the PARENT so a delivery team keeps its site while
  // moving between tabs — re-picking it on every tab switch is the kind of friction that makes a
  // filter go unused.
  const [warehouse, setWarehouse] = useState<number | undefined>(undefined);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);

  useEffect(() => {
    listWarehouses().then(setWarehouses);
  }, []);
  // Stable identity: PoTable takes this in a useCallback dep list, so an inline arrow would
  // re-trigger its load on every parent render.
  const onCounts = useCallback((c: PoTabCounts) => setCounts(c), []);

  const label = (key: PoTab, text: string, hint: string) => (
    <Tooltip title={hint}>
      <span>
        {text}
        {counts ? (
          <Badge
            count={counts[key]}
            showZero
            overflowCount={9999}
            style={{
              marginLeft: 8,
              backgroundColor: key === "delayed" && counts[key] > 0 ? "#cf1322" : "#8c8c8c",
            }}
          />
        ) : null}
      </span>
    </Tooltip>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <ShoppingOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>OMS PO</h2>
      </div>

      <div style={{ marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: "#8c8c8c", marginRight: 8 }}>
          Fulfilment warehouse
        </span>
        <Select
          value={warehouse}
          onChange={setWarehouse}
          allowClear
          placeholder="All warehouses"
          style={{ width: 260 }}
          options={warehouses.map((w) => ({
            value: w.id,
            label: w.city ? `${w.label} · ${w.city}` : w.label,
          }))}
        />
      </div>

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as PoPageTab)}
        items={[
          {
            key: SEARCH_TAB,
            // No badge: Search is not a set of allotments, it is a question. The `all` cut behind it
            // is what a lookup needs — a PO has to be findable whatever state it is in.
            label: (
              <Tooltip title="Find one allotment by PO number or ENQ — across every PO, whatever its state. Nothing loads until you type.">
                <span>Search</span>
              </Tooltip>
            ),
            children: <PoTable tab="all" searchOnly warehouseId={warehouse} onCounts={onCounts} />,
          },
          {
            key: "pending_delivery",
            label: label(
              "pending_delivery",
              "Pending delivery",
              "Goods not yet with the customer — an outbound leg still open. Delivered and cancelled allotments drop off.",
            ),
            children: (
              <PoTable tab="pending_delivery" warehouseId={warehouse} onCounts={onCounts} />
            ),
          },
          {
            key: "delayed",
            label: label(
              "delayed",
              "Delayed",
              "Pending delivery, and the committed delivery date has already passed. Delivered-late allotments are history, so they are not counted here.",
            ),
            children: <PoTable tab="delayed" warehouseId={warehouse} onCounts={onCounts} />,
          },
          {
            key: "due_today",
            label: label(
              "due_today",
              "Due today",
              "Pending delivery, committed for today (IST)",
            ),
            children: <PoTable tab="due_today" warehouseId={warehouse} onCounts={onCounts} />,
          },
          {
            key: "all",
            label: label("all", "All", "Every allotment, newest first"),
            children: <PoTable tab="all" warehouseId={warehouse} onCounts={onCounts} />,
          },
        ]}
      />
    </div>
  );
};

export default OmsPoDashboard;
